// The exported code uses Tailwind CSS. Install Tailwind CSS in your dev environment to ensure all styles work.
import React, { useState, useEffect } from "react";
import Header from "../../component/_elements/Header.tsx";
import { useOutletContext } from "react-router-dom";
import { OutletContextType } from "../../types/GlobalType.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { login as loginAPI } from "../../_api/auth.js";

import {
  PaiementType,
  paymentController,
} from "../../_api/controllers/payment.controller.ts";
import {
  classeController,
  montantController,
  motifPaiementController,
  promotionController,
  schoolYearController,
} from "../../_api/controllers/scolarite.controller.ts";
import { studentControler } from "../../_api/controllers/student.controller.ts";
import { capitalizeFirstLetter } from "../../_services/utils.jsx";
import moment from "moment";
import { useAuth } from "../../_api/auth.js";
import toast from "react-hot-toast";
import MyLoader from "../../component/loader/MyLoader.tsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

// Coordonnées affichées dans l'en-tête des exports (PDF/Excel).
// TODO: remplacer le numéro de téléphone par le vrai contact de l'établissement.
const SCHOOL_INFO = {
  name: "ETPM",
  fullName: "Ecole Technique Professionnelle Des Metiers",
  telephone: "+243810458708",
};

// skeleton component for payment management loading state
const PaymentManagementSkeleton: React.FC<{
  isDarkMode: boolean;
}> = ({ isDarkMode }) => (
  <main className="ml-20 md:ml-0 p-6 mt-16 animate-pulse">
    {/* Header */}
    <div className="mb-6 flex justify-between items-center">
      <div>
        <div
          className={`h-6 w-56 rounded mb-2 ${
            isDarkMode ? "bg-gray-700" : "bg-gray-300"
          }`}
        ></div>
        <div
          className={`h-4 w-80 rounded ${
            isDarkMode ? "bg-gray-600" : "bg-gray-200"
          }`}
        ></div>
      </div>
    </div>

    {/* Search & Filters */}
    <div className="flex flex-wrap gap-4 mb-6">
      <div
        className={`flex-grow h-10 rounded-md ${
          isDarkMode ? "bg-gray-700" : "bg-gray-300"
        }`}
      ></div>
      <div
        className={`h-10 w-48 rounded-md ${
          isDarkMode ? "bg-gray-700" : "bg-gray-300"
        }`}
      ></div>
      <div
        className={`h-10 w-48 rounded-md ${
          isDarkMode ? "bg-gray-700" : "bg-gray-300"
        }`}
      ></div>
    </div>

    {/* TABLE SKELETON */}
    <div
      className={`rounded-lg overflow-hidden border mb-6 ${
        isDarkMode ? "border-[#30363D]" : "border-gray-200"
      }`}
    >
      <div className="w-full overflow-x-auto">
        <table className="min-w-full">
          <thead className={isDarkMode ? "bg-[#161B22]" : "bg-gray-200"}>
            <tr>
              {[...Array(7)].map((_, i) => (
                <th key={i} className="px-6 py-4">
                  <div
                    className={`h-4 rounded w-full ${
                      isDarkMode ? "bg-gray-700" : "bg-gray-300"
                    }`}
                  ></div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody
            className={`divide-y ${
              isDarkMode
                ? "bg-[#0D1117] divide-gray-700"
                : "bg-white divide-gray-200"
            }`}
          >
            {[...Array(10)].map((_, row) => (
              <tr key={row}>
                {[...Array(7)].map((_, col) => (
                  <td key={col} className="px-6 py-4">
                    <div
                      className={`h-4 rounded w-full ${
                        isDarkMode ? "bg-gray-700" : "bg-gray-200"
                      }`}
                    ></div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </main>
);

const Payment: React.FC = () => {
  const { isDarkMode, setIsDarkMode } = useOutletContext<OutletContextType>();
  const [currentYear, setCurrentYear] = useState("2025 - 2026");
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClass, setSelectedClass] = useState("Toutes les classes");
  const [selectedStatus, setSelectedStatus] = useState("Tous les statuts");
  const [selectedDate, setSelectedDate] = useState("Toutes les dates");
  const [selectedAmount, setSelectedAmount] = useState("");
  // Filtre Année scolaire : null = non touché par l'utilisateur, auquel cas on retombe
  // sur l'année scolaire active (selectedSchoolYear?.designation) par défaut.
  const [selectedSchoolYearFilter, setSelectedSchoolYearFilter] = useState<
    string | null
  >(null);
  const [isSchoolYearFilterDropdownOpen, setIsSchoolYearFilterDropdownOpen] =
    useState(false);
  const [isClassDropdownOpen, setIsClassDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [paymentToCancel, setPaymentToCancel] = useState<any>(null);

  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  type PaymentAction = "create" | "cancel";
  const [pendingPaiementAction, setPendingPaiementAction] =
    useState<PaymentAction | null>(null);
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);

  const [createPaymentForm, setcreatePaymentForm] = useState<{
    date: string;
    amount: any;
    type: any;
    method: string;
    status: any;
    selectedStudentId: any;
    isCreation: boolean;
  }>({
    date: "",
    amount: "",
    type: 1,
    method: "Carte",
    status: 1,
    selectedStudentId: "",
    isCreation: false,
  });

  useState(false);
  const [password, setPassword] = useState("");
  const [notification, setNotification] = useState<any>(null); // { message, type }

  // Fonction pour afficher une notification
  const showNotification = (message: string, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // connected user
  const { user } = useAuth();

  const queryClient = useQueryClient();

  //Save new payement
  const mutationCreatePayment = useMutation({
    mutationFn: () =>
      paymentController.create({
        montant: createPaymentForm.amount,
        jour: moment().format("YYYY-MM-DD HH:mm:ss"),
        motif_paiement_id: 1,
        // motif_paiement_id: createPaymentForm.type,
        eleve_id: createPaymentForm.selectedStudentId,
        personnel_id: user.personnel_id, //Id du prefet
        annee_scolaire_id: selectedSchoolYear?.id,
        operation_id: `INV-${moment().year()}-00${moment().format(
          "DDMMYYYYHHMMSS",
        )}`,
        uid_terminal: "878da08c4808b62c",
      }),
    onSuccess: (data) => {
      showNotification(" Nouveau paiement effectué avec succès!", "success");
      setcreatePaymentForm({
        date: "",
        amount: "",
        type: "",
        method: "Espèce",
        status: 1,
        selectedStudentId: "",
        isCreation: false,
      });
      queryClient.invalidateQueries({ queryKey: ["payments"] });
    },

    onError: (err: any) => {
      toast.error(err.response?.data?.message || "Une erreur est survenue", {
        duration: 4000,
        position: "bottom-right",
        style: {
          background: "red",
          color: "#fff",
          padding: "10px",
          fontSize: "14px",
          borderRadius: "5px",
        },
      });

      setcreatePaymentForm({
        date: "",
        amount: "",
        type: "",
        method: "Espèce",
        status: 1,
        selectedStudentId: "",
        isCreation: false,
      });
    },
  });

  const mutationAnnulerPayment = useMutation({
    mutationFn: () => paymentController.desactivate(paymentToCancel.id),
    onSuccess: (data) => {
      // console.log("desactivate Mutation", data);
      setShowCancelModal(false);
      showNotification(" Annulation effectuée avec succès!", "success");

      setPaymentToCancel(null);
      queryClient.invalidateQueries({ queryKey: ["payments"] });
    },

    onError: (err) => {
      // console.log(err);
      showNotification(
        " Une erreur est survenue lors de l'annulation!",
        "error",
      );
    },
  });

  const paymentActions: Record<PaymentAction, () => void> = {
    create: mutationCreatePayment.mutate,
    cancel: mutationAnnulerPayment.mutate,
  };

  const mutationLogin = useMutation({
    mutationFn: loginAPI,
    onSuccess: (data) => {
      if (data.token) {
        //after password correct call apdate function mutation
        if (!pendingPaiementAction) return;

        paymentActions[pendingPaiementAction]();

        setPendingPaiementAction(null);
      } else
        return toast.error("Mot de passe incorrect, veuillez réessayer.", {
          duration: 4000,
          position: "bottom-right",
          style: {
            background: "red",
            color: "#fff",
            padding: "10px",
            fontSize: "14px",
            borderRadius: "5px",
          },
        });
    },

    onError: (error) => {
      // console.log(error);
      setPassword("");
      return;
    },
  });

  // Étape 2 : vérification du mot de passe
  const handleConfirmPassword = () => {
    if (!password)
      return showNotification("Veuillez compléter le mot de passe", "error");
    if (!user.email)
      return showNotification("Une erreur s'est produite, actualiser", "error");
    //call the login & update mutation functions
    mutationLogin.mutate({ email: user.email, password });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dropdown = document.getElementById("export-dropdown");
      const button = document.getElementById("export-button");
      if (
        dropdown &&
        button &&
        !button.contains(event.target as Node) &&
        !dropdown.contains(event.target as Node)
      ) {
        dropdown.style.display = "none";
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // console.log(paymentAmount);
  const [currentPage, setCurrentPage] = useState(1);
  const paymentsPerPage = 500;

  const handleReceiptClick = (payment: any) => {
    setSelectedPayment(payment);
    setShowReceiptModal(true);
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };
  const toggleYearDropdown = () => {
    setIsYearDropdownOpen(!isYearDropdownOpen);
  };
  const toggleClassDropdown = () => {
    setIsClassDropdownOpen(!isClassDropdownOpen);
    if (isClassDropdownOpen) {
      setIsStatusDropdownOpen(false);
      setIsDateDropdownOpen(false);
      setIsSchoolYearFilterDropdownOpen(false);
    }
  };
  const toggleStatusDropdown = () => {
    setIsStatusDropdownOpen(!isStatusDropdownOpen);
    if (isStatusDropdownOpen) {
      setIsClassDropdownOpen(false);
      setIsDateDropdownOpen(false);
      setIsSchoolYearFilterDropdownOpen(false);
    }
  };
  const toggleDateDropdown = () => {
    setIsDateDropdownOpen(!isDateDropdownOpen);
    if (isDateDropdownOpen) {
      setIsClassDropdownOpen(false);
      setIsStatusDropdownOpen(false);
      setIsSchoolYearFilterDropdownOpen(false);
    }
  };
  const toggleSchoolYearFilterDropdown = () => {
    setIsSchoolYearFilterDropdownOpen(!isSchoolYearFilterDropdownOpen);
    if (isSchoolYearFilterDropdownOpen) {
      setIsClassDropdownOpen(false);
      setIsStatusDropdownOpen(false);
      setIsDateDropdownOpen(false);
    }
  };

  //

  //fetch all students
  const { data: studentdata, isLoading: isStudentLoading } = useQuery({
    queryKey: ["students"],
    queryFn: studentControler.getAll,
  });
  //fetch all payment data
  const { data: paiementData, isLoading: isPaymentLoading } = useQuery({
    queryKey: ["payments"],
    queryFn: paymentController.getAll,
  });

  const { data: promotionData, isLoading: isPromotionLoading } = useQuery({
    queryKey: ["promotions"],
    queryFn: promotionController.getAll,
  });
  const { data: classeData, isLoading: isClasseLoading } = useQuery({
    queryKey: ["classes"],
    queryFn: classeController.getAll,
  });

  //fetch anne scolaire
  const { data: schoolYear, isLoading: isSchoolYearLoading } = useQuery({
    queryKey: ["schoolYears"],
    queryFn: schoolYearController.getAll,
  });

  // fetch motif Paiement
  const { data: motifPaiementData, isLoading: isMotifPaiementLoading } =
    useQuery({
      queryKey: ["motifPaiement"],
      queryFn: motifPaiementController.getAll,
    });

  // fetch Montants frais scolaire for selected school year
  const { data: montantPerClasse } = useQuery({
    queryKey: ["montantPerClasse"],
    queryFn: montantController.getAll,
  });
  const montantPerClasseArr = montantPerClasse?.data || [];
  const classeDataArr = classeData?.data ? classeData?.data : [];
  const schoolYearArr = schoolYear?.data || [];
  const selectedSchoolYear = schoolYearArr.find((sy: any) => sy.selected);

  // Valeur effective du filtre Année scolaire : tant que l'utilisateur n'a rien
  // choisi explicitement (null), on utilise l'année scolaire active par défaut.
  const effectiveSchoolYearFilter =
    selectedSchoolYearFilter ??
    selectedSchoolYear?.designation ??
    "Toutes les années";

  const promotionDataArr = promotionData?.data || [];
  const paymentDataArray = paiementData?.data || [];
  const studentDataArr = studentdata?.data || [];
  const motifPaiementArr = motifPaiementData?.data || [];

  const studentsNowAffected = studentDataArr
    .map((student: any) => {
      let promotionData: any = null;
      let classeDesignation: any = null;

      for (const promotion of promotionDataArr) {
        if (
          promotion.annee_scolaire_id === selectedSchoolYear?.id &&
          promotion.eleve_id === student.eleve_id
        ) {
          promotionData = promotion;
          break;
        }
      }

      for (const classe of classeDataArr) {
        if (promotionData?.classe_id === classe.id) {
          classeDesignation = classe.designation;
          break;
        }
      }

      return {
        student,
        classeDesignation,
      };
    })
    .filter((item: any) => item.classeDesignation);

  // Get paiement informations of selected student
  const getPaiementDetailForSelectedStudent = () => {
    let classe_id: any;
    let annualTotal: any;
    //if selectedPayment is null it means we are register newPayment
    // if it not null it means we are updating
    let selected_student_id: number = !selectedPayment
      ? parseInt(createPaymentForm.selectedStudentId)
      : parseInt(selectedPayment.eleve_id);

    if (!selected_student_id)
      return {
        total: 0,
        balance: 0,
        paid: 0,
        percentage: 0,
      };

    promotionDataArr.map((item) => {
      if (
        item.annee_scolaire_id === selectedSchoolYear?.id &&
        item.eleve_id === selected_student_id
      ) {
        classe_id = item.classe_id;
      }
    });
    montantPerClasseArr.map((item) => {
      if (
        item.annee_scolaire_id === selectedSchoolYear?.id &&
        item.classe_id === classe_id
      ) {
        annualTotal = item.montant;
      }
    });

    const filteredPaiementsForStudent = paymentDataArray.filter((item) => {
      const matchStudent = item.eleve_id === selected_student_id;
      const matchSchoolYear = item.annee_scolaire_id === selectedSchoolYear?.id;
      return matchStudent && matchSchoolYear;
    });

    const paiedTotal = filteredPaiementsForStudent
      .filter((item) => item.status === 1)
      .reduce((sum, paiement) => sum + paiement.montant, 0);
    const balanceTotal = annualTotal - paiedTotal;

    const percentage =
      annualTotal === 0 ? 0 : ((paiedTotal * 100) / annualTotal).toFixed(2);

    return {
      total: annualTotal,
      balance: balanceTotal,
      paid: paiedTotal,
      percentage,
    };
  };

  // console.log(getPaiementDetailForSelectedStudent());
  // console.log(selectedPayment);

  //Create explicite dataArray for students
  let completePayementsArray: PaiementType &
    any &
    {
      eleve: string;
      type: string;
      classe: string;
      montant: any;
      date: string;
      statut: string;
      methode: string;
      id: any;
    }[] = paymentDataArray.map((payment: PaiementType) => {
    let student_data: any,
      promotion_data: any,
      class_data: any,
      motif_designation: any,
      classeIdOfPayment: any,
      classeDesignationOfPayment: any;

    for (const student of studentDataArr) {
      if (student.eleve_id === payment.eleve_id) {
        student_data = student;
      }
    }
    for (const motif of motifPaiementArr) {
      if (motif.id === payment.motif_paiement_id) {
        motif_designation = motif.designation;
      }
    }

    for (const promotion of promotionDataArr) {
      if (
        promotion.annee_scolaire_id === selectedSchoolYear?.id &&
        promotion.eleve_id === payment.eleve_id
      ) {
        promotion_data = promotion;
        //it is possible to get all promotion of the current student here by handling the condition above
      }

      //get the classeId in promotion data then get the class designation as we have payement school year id and student id in promotion data
      if (
        promotion.annee_scolaire_id === payment.annee_scolaire_id &&
        promotion.eleve_id === payment.eleve_id
      ) {
        classeIdOfPayment = promotion.classe_id;
      }
    }

    for (const classe of classeDataArr) {
      if (promotion_data?.classe_id === classe.id) {
        class_data = classe.designation;
      }
      if (classeIdOfPayment === classe.id) {
        classeDesignationOfPayment = classe.designation;
      }
    }

    //add schoolyear designation to paiement data
    const schoolYear = schoolYearArr.find(
      (sy: any) => sy.id === payment.annee_scolaire_id,
    );

    return {
      ...payment,
      annee_scolaire_designation: schoolYear ? schoolYear.designation : "N/A",
      eleve: `${student_data?.nom} ${
        student_data?.postnom
      } ${capitalizeFirstLetter(student_data?.prenom)}`,
      sexe: student_data?.sexe,
      classe: capitalizeFirstLetter(class_data),
      classeDesignationOfPayment: capitalizeFirstLetter(
        classeDesignationOfPayment,
      ),
      promotionData: promotionData,
      type: motif_designation,
      methode: "Espèce", //default value for methode
      statut: payment.status ? "Payé" : "Annulé", //default value for statut
      date: moment(payment.created_at).format("DD/MM/YYYY"),
    };
  });

  const payments = [...completePayementsArray];

  // console.log(completePayementsArray);
  // Filtrer les paiements selon la recherche et les filtres
  const filteredPayments = payments.filter((payment) => {
    const matchesSearch =
      payment.eleve.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payment.type.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesClass =
      selectedClass === "Toutes les classes" ||
      payment.classe.toLocaleLowerCase() === selectedClass.toLowerCase();
    const matchesStatus =
      selectedStatus === "Tous les statuts" ||
      payment.statut === selectedStatus;

    const matchesDate =
      selectedDate === "Toutes les dates" ||
      payment.date.includes(selectedDate);
    const matchesAmount =
      !selectedAmount ||
      payment.montant.replace(/[^0-9,]/g, "") ===
        selectedAmount.replace(/[^0-9,]/g, "");
    const matchesSchoolYear =
      effectiveSchoolYearFilter === "Toutes les années" ||
      payment.annee_scolaire_designation === effectiveSchoolYearFilter;
    return (
      matchesSearch &&
      matchesClass &&
      matchesStatus &&
      matchesDate &&
      matchesAmount &&
      matchesSchoolYear
    );
  });

  // console.log("filteredPayments", filteredPayments);

  const indexOfLastPayment = currentPage * paymentsPerPage;
  const indexOfFirstPayment = indexOfLastPayment - paymentsPerPage;
  const currentPayments = filteredPayments.slice(
    indexOfFirstPayment,
    indexOfLastPayment,
  );
  const totalPages = Math.ceil(filteredPayments.length / paymentsPerPage);
  const handlePageChange = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleChangeDate = (e: any) => {
    const selectedDateValue = e.target.value;
    if (selectedDateValue) {
      setSelectedDate(moment(selectedDateValue).format("DD/MM/YYYY"));
      setIsDateDropdownOpen(false);
    }
  };

  const handleResetFilter = () => {
    setSelectedClass("Toutes les classes");
    setSelectedStatus("Tous les statuts");
    setSelectedDate("Toutes les dates");
    setSelectedSchoolYearFilter(null);
  };

  const toggleExportDropdown = () => {
    setIsExportDropdownOpen(!isExportDropdownOpen);
  };

  //Handle mouse click outside for dropdowns
  const ExportDropdownRef = React.useRef<HTMLDivElement>(null);
  const dateDropdownRef = React.useRef<HTMLDivElement>(null);
  const classDropdownRef = React.useRef<HTMLDivElement>(null);
  const statusDropdownRef = React.useRef<HTMLDivElement>(null);
  const schoolYearFilterDropdownRef = React.useRef<HTMLDivElement>(null);
  // Fermeture des menus lors d'un clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      //export button
      if (
        ExportDropdownRef.current &&
        !ExportDropdownRef.current.contains(event.target as Node)
      ) {
        setIsExportDropdownOpen(false);
      }
      //export button
      if (
        dateDropdownRef.current &&
        !dateDropdownRef.current.contains(event.target as Node)
      ) {
        setIsDateDropdownOpen(false);
      }

      //class button
      if (
        classDropdownRef.current &&
        !classDropdownRef.current.contains(event.target as Node)
      ) {
        setIsClassDropdownOpen(false);
      }

      //status button
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target as Node)
      ) {
        setIsStatusDropdownOpen(false);
      }

      //school year filter button
      if (
        schoolYearFilterDropdownRef.current &&
        !schoolYearFilterDropdownRef.current.contains(event.target as Node)
      ) {
        setIsSchoolYearFilterDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const dropdownPanelClass = `absolute top-full right-0 mt-2 w-52 rounded-xl shadow-2xl overflow-hidden text-xs ${
    isDarkMode ? "bg-[#161B22]" : "bg-white"
  } z-30 border ${isDarkMode ? "border-[#30363D]" : "border-gray-200"}`;

  const dropdownItemClass = `flex items-center w-full px-3.5 py-2.5 text-xs text-left transition-colors ${
    isDarkMode ? "hover:bg-[#21262D]" : "hover:bg-gray-50"
  } !rounded-button whitespace-nowrap`;

  const filterButtonClass = `h-10 flex items-center px-3.5 rounded-lg text-xs font-medium transition-all ${
    isDarkMode
      ? "bg-[#161B22] hover:bg-[#21262D] text-gray-300 border-[#30363D]"
      : "bg-white hover:bg-gray-50 text-gray-700 border-gray-200"
  } border !rounded-button whitespace-nowrap`;

  // Affiche une notification éphémère en bas à droite de l'écran
  const showExportNotification = (
    message: string,
    type: "success" | "erreur",
  ) => {
    const el = document.createElement("div");
    el.className = `fixed bottom-4 right-4 px-6 py-3 rounded-lg text-white ${
      type === "success"
        ? isDarkMode
          ? "bg-green-600"
          : "bg-green-500"
        : isDarkMode
          ? "bg-red-600"
          : "bg-red-500"
    } shadow-lg transition-opacity duration-500`;
    el.innerHTML = `<i class="fas ${
      type === "success" ? "fa-check-circle" : "fa-circle-exclamation"
    } mr-2"></i>${message}`;
    document.body.appendChild(el);
    setTimeout(() => {
      el.style.opacity = "0";
      setTimeout(() => el.remove(), 500);
    }, 3000);
  };

  // Titre du document, basé sur le filtre classe + le filtre année scolaire actifs
  const getExportTitle = () => {
    const classeLabel = selectedClass;
    const anneeLabel = effectiveSchoolYearFilter;
    return `Liste de paiement des élèves — ${classeLabel} — ${anneeLabel}`;
  };

  // Dessine l'en-tête professionnel commun (logo textuel + coordonnées) sur le PDF
  const drawPdfHeader = (doc: jsPDF) => {
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(37, 99, 235);
    doc.text(SCHOOL_INFO.name, 14, 18);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(SCHOOL_INFO.fullName, 14, 24);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text(`Tél: ${SCHOOL_INFO.telephone}`, pageWidth - 14, 18, {
      align: "right",
    });

    doc.setDrawColor(210, 210, 210);
    doc.line(14, 28, pageWidth - 14, 28);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(20, 20, 20);
    doc.text(getExportTitle(), pageWidth / 2, 38, { align: "center" });

    return 44;
  };

  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const startY = drawPdfHeader(doc);

    const rows = filteredPayments
      .sort((a, b) =>
        a.eleve.toLowerCase().localeCompare(b.eleve.toLowerCase()),
      )
      .map((payment: any, index: number) => [
        `${index + 1} `,
        `${payment.eleve}`,
        payment.classe || "-",
        payment.sexe === "F" ? "Féminin" : "Masculin",
        `${payment.montant} $`,
        payment.date,
        payment.status ? "Payé" : "Annulé",
      ]);

    autoTable(doc, {
      startY,
      head: [
        [
          "N°",
          "Nom",
          "Classe",
          "Genre",
          "Montant payé",
          "Date de paiement",
          "Status",
        ],
      ],
      body: rows,
      theme: "striped",
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: 255,
        fontStyle: "bold",
      },
      styles: { fontSize: 9, cellPadding: 3 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      margin: { left: 14, right: 14 },
    });

    const pageCount = doc.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Généré le ${moment().format("DD/MM/YYYY à HH:mm")} - ChikSoft`,
        14,
        pageHeight - 8,
      );
      doc.text(`Page ${i} / ${pageCount}`, pageWidth - 14, pageHeight - 8, {
        align: "right",
      });
    }

    doc.save(`liste_paiements_${moment().format("YYYY-MM-DD")}.pdf`);
  };

  const exportToExcel = () => {
    const rows = filteredPayments.map((payment: any) => ({
      Nom: payment.eleve,
      Classe: payment.classe || "-",
      Genre: payment.sexe === "F" ? "Féminin" : "Masculin",
      "Montant payé": `${payment.montant} $`,
      "Date de paiement": payment.date,
      Status: payment.status ? "Payé" : "Annulé",
    }));

    const headerRows = [
      [SCHOOL_INFO.name],
      [SCHOOL_INFO.fullName],
      [`Tél: ${SCHOOL_INFO.telephone}`],
      [],
      [getExportTitle()],
      [],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(headerRows);
    XLSX.utils.sheet_add_json(worksheet, rows, { origin: headerRows.length });

    worksheet["!cols"] = [
      { wch: 32 },
      { wch: 18 },
      { wch: 12 },
      { wch: 16 },
      { wch: 18 },
    ];
    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 4 } },
      { s: { r: 4, c: 0 }, e: { r: 4, c: 4 } },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Paiements");
    XLSX.writeFile(
      workbook,
      `liste_paiements_${moment().format("YYYY-MM-DD")}.xlsx`,
    );
  };

  const exportData = (format: "PDF" | "Excel") => {
    if (filteredPayments.length === 0) {
      showExportNotification(
        "Aucun paiement à exporter pour ce filtre.",
        "erreur",
      );
      setIsExportDropdownOpen(false);
      return;
    }

    try {
      if (format === "PDF") {
        exportToPDF();
      } else {
        exportToExcel();
      }
      showExportNotification(
        format === "PDF" ? "Export PDF réussi" : "Export Excel réussi",
        "success",
      );
    } catch (err) {
      showExportNotification("L'export a échoué, réessayez.", "erreur");
    }

    setIsExportDropdownOpen(false);
  };

  return (
    // <div className="md:ml-60 w-full">
    <div
      className={`md:ml-60 md:w-[calc(100%-15rem)] min-h-screen ${isDarkMode ? "bg-[#0D1117]" : "bg-[#F8FAFC]"}`}
    >
      {/* Header */}
      <Header
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        toggleDarkMode={toggleDarkMode}
        currentYear={currentYear}
        isYearDropdownOpen={isYearDropdownOpen}
        toggleYearDropdown={toggleYearDropdown}
        setCurrentYear={setCurrentYear}
        setIsYearDropdownOpen={setIsYearDropdownOpen}
      />
      {/* Payments Management Content */}

      {isClasseLoading ||
      isStudentLoading ||
      isPaymentLoading ||
      isPromotionLoading ||
      isSchoolYearLoading ||
      isMotifPaiementLoading ? (
        <PaymentManagementSkeleton isDarkMode={isDarkMode} />
      ) : (
        <main className="ml-20 md:ml-0 px-4 py-5 sm:px-5 lg:px-6 lg:py-6 mt-16">
          <div className="mb-5 flex flex-col justify-start gap-4 lg:flex-row lg:justify-between lg:items-center">
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  isDarkMode
                    ? "bg-blue-500/10 text-blue-400"
                    : "bg-blue-50 text-blue-600"
                }`}
              >
                <i className="fas fa-cash-register text-sm"></i>
              </div>
              <div>
                <h1 className="text-base font-semibold leading-tight">
                  Gestion des paiements
                </h1>
                <p
                  className={`text-[11px] mt-1 ${
                    isDarkMode ? "text-gray-500" : "text-gray-500"
                  }`}
                >
                  Gérez les transactions financières et générez des reçus
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAddModal(true)}
                className={`h-10 flex items-center px-4 rounded-lg text-xs font-semibold shadow-sm transition-all duration-200 ${
                  isDarkMode
                    ? "bg-blue-600 hover:bg-blue-500"
                    : "bg-blue-600 hover:bg-blue-700"
                } text-white !rounded-button whitespace-nowrap`}
              >
                <i className="fas fa-plus mr-2"></i>
                <span className="hidden lg:flex text-xs">Nouveau paiement</span>
              </button>

              <div className="relative" ref={ExportDropdownRef}>
                <button
                  onClick={toggleExportDropdown}
                  className={`h-10 px-4 rounded-lg border flex items-center gap-2
      text-xs font-medium transition ${
        isDarkMode
          ? "bg-[#161B22] border-[#30363D] hover:bg-[#21262D] text-gray-300"
          : "bg-white border-gray-200 hover:bg-gray-50 text-gray-600"
      }`}
                >
                  <i className="fas fa-download text-[10px]" />

                  <span>Exporter</span>

                  <i
                    className={`fas fa-chevron-down text-[8px] ml-1 transition-transform duration-200 ${
                      isExportDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isExportDropdownOpen && (
                  <div
                    className={`absolute right-0 top-full mt-2 w-52 rounded-xl border
        shadow-xl overflow-hidden z-30 ${
          isDarkMode
            ? "bg-[#161B22] border-[#30363D]"
            : "bg-white border-gray-200"
        }`}
                  >
                    <div className="p-1.5">
                      {/* PDF */}
                      <button
                        onClick={() => exportData("PDF")}
                        className={`w-full px-3 py-2.5 rounded-lg flex items-center
            gap-3 text-xs text-left transition ${
              isDarkMode ? "hover:bg-[#21262D]" : "hover:bg-gray-50"
            }`}
                      >
                        <div
                          className="w-8 h-8 rounded-lg bg-red-500/10 text-red-500
              flex items-center justify-center flex-shrink-0"
                        >
                          <i className="fas fa-file-pdf text-xs" />
                        </div>

                        <div className="min-w-0">
                          <p
                            className={`font-medium ${
                              isDarkMode ? "text-gray-200" : "text-gray-700"
                            }`}
                          >
                            Format PDF
                          </p>

                          <p className="text-[9px] text-gray-500 mt-0.5">
                            Document imprimable
                          </p>
                        </div>
                      </button>

                      {/* Excel */}
                      <button
                        onClick={() => exportData("Excel")}
                        className={`w-full px-3 py-2.5 rounded-lg flex items-center
            gap-3 text-xs text-left transition ${
              isDarkMode ? "hover:bg-[#21262D]" : "hover:bg-gray-50"
            }`}
                      >
                        <div
                          className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-500
              flex items-center justify-center flex-shrink-0"
                        >
                          <i className="fas fa-file-excel text-xs" />
                        </div>

                        <div className="min-w-0">
                          <p
                            className={`font-medium ${
                              isDarkMode ? "text-gray-200" : "text-gray-700"
                            }`}
                          >
                            Format Excel
                          </p>

                          <p className="text-[9px] text-gray-500 mt-0.5">
                            Feuille de calcul
                          </p>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Search and Filters */}
          <div
            className={`flex flex-wrap items-center gap-2.5 mb-5 p-3.5 rounded-xl border ${
              isDarkMode
                ? "bg-[#0D1117] border-[#30363D]"
                : "bg-white border-gray-200 shadow-sm"
            }`}
          >
            <div className="relative flex-1 min-w-[240px]">
              <div
                className={`absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400`}
              >
                <i className="fas fa-search text-sm"></i>
              </div>
              <input
                type="text"
                placeholder="Rechercher un élève..."
                className={`w-full h-10 pl-10 pr-4 rounded-lg text-xs transition-all ${
                  isDarkMode
                    ? "bg-[#161B22] border-[#30363D] text-gray-200 placeholder:text-gray-500"
                    : "bg-white border-gray-200 text-gray-800 placeholder:text-gray-400"
                } border focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            {/* Année scolaire Filter */}
            <div className="relative" ref={schoolYearFilterDropdownRef}>
              <button
                onClick={toggleSchoolYearFilterDropdown}
                className={filterButtonClass}
              >
                <i className="fas fa-calendar-alt mr-2 text-xs opacity-70"></i>
                <span>{effectiveSchoolYearFilter}</span>
                <i className="fas fa-chevron-down ml-2 text-xs"></i>
              </button>
              {isSchoolYearFilterDropdownOpen && (
                <div className={dropdownPanelClass}>
                  <div className="py-1 max-h-64 overflow-y-auto">
                    <button
                      onClick={() => {
                        setSelectedSchoolYearFilter("Toutes les années");
                        setIsSchoolYearFilterDropdownOpen(false);
                      }}
                      className={dropdownItemClass}
                    >
                      Toutes les années
                    </button>

                    {schoolYearArr.map((year: any) => {
                      // console.log(year);
                      return (
                        <button
                          key={year.id}
                          onClick={() => {
                            setSelectedSchoolYearFilter(year.designation);
                            setIsSchoolYearFilterDropdownOpen(false);
                          }}
                          className={dropdownItemClass}
                        >
                          {year.designation}
                          {!!year.selected && (
                            <span
                              className={`ml-2 text-xs ${
                                isDarkMode ? "text-blue-400" : "text-blue-600"
                              }`}
                            >
                              (active)
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            {/* Date Filter */}
            <div className="relative">
              <button
                onClick={toggleDateDropdown}
                className={filterButtonClass}
              >
                <i className="fas fa-calendar-day mr-2 text-xs opacity-70"></i>
                <span>{selectedDate}</span>
                <i className="fas fa-chevron-down ml-2 text-xs"></i>
              </button>
              {isDateDropdownOpen && (
                <div className={dropdownPanelClass} ref={dateDropdownRef}>
                  <div className="py-1 space-y-2 p-2">
                    <button
                      onClick={() => {
                        setSelectedDate("Toutes les dates");
                        setIsDateDropdownOpen(false);
                      }}
                      className={`block px-4 py-2.5 text-xs w-full text-left rounded-md ${
                        isDarkMode ? "hover:bg-[#21262D]" : "hover:bg-gray-50"
                      } !rounded-button whitespace-nowrap`}
                    >
                      Toutes les dates
                    </button>
                    <input
                      type="date"
                      onChange={handleChangeDate}
                      className={`block px-4 py-2 text-xs w-full text-left rounded-md border ${
                        isDarkMode
                          ? "bg-[#0D1117] border-gray-700 hover:bg-gray-800"
                          : "border-gray-200 hover:bg-gray-50"
                      } !rounded-button whitespace-nowrap`}
                    />
                  </div>
                </div>
              )}
            </div>
            {/* Class Filter */}
            <div className="relative">
              <button
                onClick={toggleClassDropdown}
                className={filterButtonClass}
              >
                <i className="fas fa-chalkboard mr-2 text-xs opacity-70"></i>
                <span>{selectedClass}</span>
                <i className="fas fa-chevron-down ml-2 text-xs"></i>
              </button>
              {isClassDropdownOpen && (
                <div className={dropdownPanelClass} ref={classDropdownRef}>
                  <div className="py-1 max-h-64 overflow-y-auto">
                    <button
                      onClick={() => {
                        setSelectedClass("Toutes les classes");
                        setIsClassDropdownOpen(false);
                      }}
                      className={dropdownItemClass}
                    >
                      Toutes les classes
                    </button>

                    {classeDataArr.map((classe) => (
                      <button
                        key={classe.id}
                        onClick={() => {
                          setSelectedClass(classe.designation);
                          setIsClassDropdownOpen(false);
                        }}
                        className={dropdownItemClass}
                      >
                        {capitalizeFirstLetter(classe.designation)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Status Filter */}
            <div className="relative">
              <button
                onClick={toggleStatusDropdown}
                className={filterButtonClass}
              >
                <i className="fas fa-circle-dot mr-2 text-xs opacity-70"></i>
                <span>{selectedStatus}</span>
                <i className="fas fa-chevron-down ml-2 text-xs"></i>
              </button>
              {isStatusDropdownOpen && (
                <div className={dropdownPanelClass} ref={statusDropdownRef}>
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setSelectedStatus("Tous les statuts");
                        setIsStatusDropdownOpen(false);
                      }}
                      className={dropdownItemClass}
                    >
                      Tous les statuts
                    </button>
                    <button
                      onClick={() => {
                        setSelectedStatus("Payé");
                        setIsStatusDropdownOpen(false);
                      }}
                      className={dropdownItemClass}
                    >
                      <span className="h-2 w-2 rounded-full bg-green-500 mr-2"></span>
                      Payé
                    </button>
                    <button
                      onClick={() => {
                        setSelectedStatus("Annulé");
                        setIsStatusDropdownOpen(false);
                      }}
                      className={dropdownItemClass}
                    >
                      <span className="h-2 w-2 rounded-full bg-red-500 mr-2"></span>
                      Annulé
                    </button>
                  </div>
                </div>
              )}
            </div>
            {/* Filter Button */}
            <button
              className={`px-2 py-1 rounded-lg ${
                isDarkMode
                  ? "bg-[#161B22] hover:bg-gray-700 border-gray-700"
                  : "bg-white hover:bg-gray-50 border-gray-300"
              } border !rounded-button whitespace-nowrap`}
              title="Reinitialiser"
              onClick={handleResetFilter}
            >
              <i className="fas fa-filter text-xs"></i>
            </button>
          </div>
          {/* Payments Table */}

          <div
            className={`w-full min-w-0 rounded-xl border overflow-hidden transition-shadow ${
              isDarkMode
                ? "bg-[#0D1117] border-[#30363D]"
                : "bg-white border-gray-200 shadow-sm"
            }`}
          >
            <div className="w-full overflow-x-auto">
              <table
                className={`w-full min-w-[940px] divide-y ${isDarkMode ? "divide-[#30363D]" : "divide-gray-200"}`}
              >
                {" "}
                <thead
                  className={`${isDarkMode ? "bg-[#161B22]" : "bg-gray-50/80"}`}
                >
                  <tr>
                    <th
                      scope="col"
                      className={`px-5 py-3 text-left text-[10px] font-semibold uppercase tracking-wider ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Élève
                    </th>
                    <th
                      scope="col"
                      className={`px-5 py-3 text-left text-[10px] font-semibold uppercase tracking-wider ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Montant
                    </th>
                    <th
                      scope="col"
                      className={`px-5 py-3 text-left text-[10px] font-semibold uppercase tracking-wider ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Date
                    </th>
                    <th
                      scope="col"
                      className={`px-5 py-3 text-left text-[10px] font-semibold uppercase tracking-wider ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Type de frais
                    </th>
                    <th
                      scope="col"
                      className={`px-5 py-3 text-left text-[10px] font-semibold uppercase tracking-wider ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Année scolaire
                    </th>
                    <th
                      scope="col"
                      className={`px-5 py-3 text-left text-[10px] font-semibold uppercase tracking-wider ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Statut
                    </th>
                    <th
                      scope="col"
                      className={`px-5 py-3 text-center text-[10px] font-semibold uppercase tracking-wider ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`${
                    isDarkMode ? "bg-[#0D1117]" : "bg-white"
                  } divide-y ${
                    isDarkMode ? "divide-[#21262D]" : "divide-gray-100"
                  }`}
                >
                  {currentPayments && currentPayments.length > 0 ? (
                    currentPayments.map((payment) => (
                      <tr
                        key={payment.id}
                        className={`${
                          isDarkMode ? "hover:bg-[#161B22]" : "hover:bg-gray-50"
                        } cursor-pointer transition-colors`}
                        onClick={() => {
                          handleReceiptClick(payment);
                        }}
                      >
                        <td className="px-5 py-3.5 whitespace-nowrap align-middle">
                          <div
                            className={`${
                              isDarkMode ? "text-gray-200" : "text-gray-800"
                            } font-semibold text-xs`}
                          >
                            {payment.eleve}
                          </div>
                          <div
                            className={`text-[11px] mt-0.5 ${
                              isDarkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            {payment.classe}
                          </div>
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap text-sm font-bold tabular-nums">
                          {payment.montant} {" $"}
                        </td>
                        <td
                          className={`px-5 py-3.5 whitespace-nowrap text-xs ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {payment.date}
                        </td>
                        <td
                          className={`px-5 py-3.5 whitespace-nowrap text-xs ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {payment.type}
                        </td>
                        <td
                          className={`px-5 py-3.5 whitespace-nowrap text-xs ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {payment.annee_scolaire_designation}
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap align-middle">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold rounded-full ${
                              payment.statut === "Payé"
                                ? isDarkMode
                                  ? "bg-green-500/10 text-green-400"
                                  : "bg-green-100 text-green-800"
                                : payment.statut === "En attente"
                                  ? isDarkMode
                                    ? "bg-yellow-500/10 text-yellow-400"
                                    : "bg-yellow-100 text-yellow-800"
                                  : isDarkMode
                                    ? "bg-red-500/10 text-red-400"
                                    : "bg-red-100 text-red-800"
                            }`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${
                                payment.statut === "Payé"
                                  ? "bg-green-500"
                                  : payment.statut === "En attente"
                                    ? "bg-yellow-500"
                                    : "bg-red-500"
                              }`}
                            ></span>
                            {payment.statut}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap text-center">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => handleReceiptClick(payment)}
                              className={`h-8 w-8 flex items-center justify-center rounded-lg border text-blue-500 transition-colors ${
                                isDarkMode
                                  ? "border-[#30363D] hover:bg-blue-500/10"
                                  : "border-gray-200 hover:bg-blue-50"
                              } cursor-pointer !rounded-button whitespace-nowrap`}
                              title="Détail"
                            >
                              <i className="fas fa-receipt text-sm"></i>
                            </button>

                            <button
                              disabled={!payment.status}
                              onClick={(e) => {
                                e.stopPropagation();
                                setPaymentToCancel(payment);
                                setShowCancelModal(true);
                              }}
                              className={`h-8 w-8 flex items-center justify-center rounded-lg border transition-colors !rounded-button whitespace-nowrap ${
                                !payment.status
                                  ? `${isDarkMode ? "border-[#30363D]" : "border-gray-200"} text-gray-400 cursor-not-allowed`
                                  : `text-red-500 cursor-pointer ${
                                      isDarkMode
                                        ? "border-[#30363D] hover:bg-red-500/10"
                                        : "border-gray-200 hover:bg-red-50"
                                    }`
                              }`}
                              title="Annuler"
                            >
                              <i className="fas fa-trash-alt text-sm"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className={`px-6 py-16 text-center ${
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }`}
                      >
                        <i className="fas fa-receipt text-2xl mb-2 block opacity-40"></i>
                        <span className="text-sm lowercase">
                          Aucun paiement trouvé.
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {/* </div> */}
          {/* Pagination */}
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-0.5">
            <p className="text-[10px] text-gray-500">
              Affichage de {indexOfFirstPayment + 1} à{" "}
              {Math.min(indexOfLastPayment, filteredPayments.length)} sur{" "}
              {filteredPayments.length} paiements
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`h-8 min-w-8 px-2.5 flex items-center justify-center rounded-lg text-xs transition-colors ${
                  isDarkMode
                    ? "bg-[#161B22] hover:bg-[#21262D]"
                    : "bg-white hover:bg-gray-50"
                } border ${
                  isDarkMode ? "border-[#30363D]" : "border-gray-200"
                } !rounded-button whitespace-nowrap ${
                  currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => handlePageChange(index + 1)}
                  className={`h-8 min-w-8 px-2.5 flex items-center justify-center rounded-lg text-xs transition-colors ${
                    currentPage === index + 1
                      ? `${
                          isDarkMode ? "bg-blue-600" : "bg-blue-600"
                        } text-white`
                      : `${
                          isDarkMode
                            ? "bg-[#161B22] hover:bg-[#21262D]"
                            : "bg-white hover:bg-gray-50"
                        } border ${
                          isDarkMode ? "border-[#30363D]" : "border-gray-200"
                        }`
                  } !rounded-button whitespace-nowrap`}
                >
                  {index + 1}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`h-8 min-w-8 px-2.5 flex items-center justify-center rounded-lg text-xs transition-colors ${
                  isDarkMode
                    ? "bg-[#161B22] hover:bg-[#21262D]"
                    : "bg-white hover:bg-gray-50"
                } border ${
                  isDarkMode ? "border-[#30363D]" : "border-gray-200"
                } !rounded-button whitespace-nowrap ${
                  currentPage === totalPages
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </main>
      )}

      {/* Receipt Modal */}
      {showReceiptModal && selectedPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/65 backdrop-blur-sm p-4">
          {/* Overlay */}
          <div
            className="absolute inset-0"
            onClick={() => setShowReceiptModal(false)}
          />

          {/* Modal */}
          <div
            className={`relative w-full max-w-2xl overflow-hidden rounded-2xl border shadow-2xl ${
              isDarkMode
                ? "border-[#30363D] bg-[#161B22]"
                : "border-gray-200 bg-white"
            }`}
          >
            {/* ================= HEADER ================= */}
            <div
              className={`flex items-center justify-between gap-4 border-b px-5 py-3.5 ${
                isDarkMode ? "border-[#30363D]" : "border-gray-200"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                    isDarkMode
                      ? "bg-blue-500/10 text-blue-400"
                      : "bg-blue-50 text-blue-600"
                  }`}
                >
                  <i className="fas fa-receipt text-xs" />
                </div>

                <div>
                  <h3
                    className={`text-sm font-semibold ${
                      isDarkMode ? "text-gray-100" : "text-gray-900"
                    }`}
                  >
                    Détail de paiement
                  </h3>

                  <p className="mt-0.5 text-[9px] text-gray-500">
                    Informations relatives à cette transaction
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {/* Date + année */}
                <div className="hidden text-right sm:block">
                  <p
                    className={`text-[10px] font-medium ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {selectedPayment.date}
                  </p>

                  <p className="mt-0.5 text-[9px] text-gray-500">
                    Année scolaire {selectedPayment?.annee_scolaire_designation}
                  </p>
                </div>

                {/* Fermer */}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedPayment(null);
                    setShowReceiptModal(false);
                  }}
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors ${
                    isDarkMode
                      ? "border-[#30363D] text-gray-400 hover:bg-[#21262D] hover:text-white"
                      : "border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  }`}
                  title="Fermer"
                >
                  <i className="fas fa-times text-[10px]" />
                </button>
              </div>
            </div>

            {/* ================= CONTENT ================= */}
            <div className="space-y-3 p-4 sm:px-5">
              {/* ================= DATE MOBILE ================= */}
              <div
                className={`flex items-center justify-between rounded-lg border px-3 py-2 sm:hidden ${
                  isDarkMode
                    ? "border-[#30363D] bg-[#0D1117]"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <i className="far fa-calendar-alt text-[10px] text-gray-500" />

                  <span
                    className={`text-[10px] ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {selectedPayment.date}
                  </span>
                </div>

                <span className="text-[9px] text-gray-500">
                  {selectedPayment?.annee_scolaire_designation}
                </span>
              </div>

              {/* ================= ELEVE + CLASSE ================= */}
              <div
                className={`grid grid-cols-1 overflow-hidden rounded-xl border sm:grid-cols-[1.5fr_1fr] ${
                  isDarkMode ? "border-[#30363D]" : "border-gray-200"
                }`}
              >
                {/* Élève */}
                <div
                  className={`flex items-center gap-3 px-4 py-3 sm:border-r ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  }`}
                >
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                      isDarkMode
                        ? "bg-blue-500/10 text-blue-400"
                        : "bg-blue-50 text-blue-600"
                    }`}
                  >
                    <i className="fas fa-user-graduate text-[10px]" />
                  </div>

                  <div className="min-w-0">
                    <p className="text-[9px] font-medium uppercase tracking-wide text-gray-500">
                      Élève
                    </p>

                    <p
                      className={`mt-0.5 truncate text-xs font-semibold ${
                        isDarkMode ? "text-gray-200" : "text-gray-800"
                      }`}
                      title={selectedPayment?.eleve}
                    >
                      {selectedPayment?.eleve}
                    </p>
                  </div>
                </div>

                {/* Classe */}
                <div
                  className={`flex items-center gap-3 border-t px-4 py-3 sm:border-t-0 ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  }`}
                >
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                      isDarkMode
                        ? "bg-[#21262D] text-gray-400"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    <i className="fas fa-school text-[10px]" />
                  </div>

                  <div className="min-w-0">
                    <p className="text-[9px] font-medium uppercase tracking-wide text-gray-500">
                      Classe
                    </p>

                    <p
                      className={`mt-0.5 truncate text-xs font-semibold ${
                        isDarkMode ? "text-gray-200" : "text-gray-800"
                      }`}
                      title={selectedPayment?.classeDesignationOfPayment}
                    >
                      {selectedPayment?.classeDesignationOfPayment}
                    </p>
                  </div>
                </div>
              </div>

              {/* ================= DETAIL TRANSACTION ================= */}
              <div
                className={`overflow-hidden rounded-xl border ${
                  isDarkMode ? "border-[#30363D]" : "border-gray-200"
                }`}
              >
                <table className="w-full">
                  <thead>
                    <tr className={isDarkMode ? "bg-[#0D1117]" : "bg-gray-50"}>
                      <th className="px-4 py-2.5 text-left text-[9px] font-semibold uppercase tracking-wide text-gray-500">
                        Description
                      </th>

                      <th className="px-4 py-2.5 text-right text-[9px] font-semibold uppercase tracking-wide text-gray-500">
                        Montant ($)
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr
                      className={`border-t ${
                        isDarkMode ? "border-[#30363D]" : "border-gray-200"
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div
                            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
                              isDarkMode
                                ? "bg-blue-500/10 text-blue-400"
                                : "bg-blue-50 text-blue-600"
                            }`}
                          >
                            <i className="fas fa-file-invoice-dollar text-[9px]" />
                          </div>

                          <span
                            className={`text-xs font-medium ${
                              isDarkMode ? "text-gray-200" : "text-gray-700"
                            }`}
                          >
                            {selectedPayment.type}
                          </span>
                        </div>
                      </td>

                      <td
                        className={`px-4 py-3 text-right text-sm font-semibold tabular-nums ${
                          isDarkMode ? "text-gray-100" : "text-gray-900"
                        }`}
                      >
                        {selectedPayment.montant}
                      </td>
                    </tr>
                  </tbody>

                  <tfoot>
                    <tr
                      className={`border-t ${
                        isDarkMode
                          ? "border-[#30363D] bg-[#0D1117]/70"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <td
                        className={`px-4 py-2.5 text-xs font-semibold ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Total
                      </td>

                      <td className="px-4 py-2.5 text-right">
                        <span
                          className={`text-sm font-bold tabular-nums ${
                            isDarkMode ? "text-emerald-400" : "text-emerald-600"
                          }`}
                        >
                          {selectedPayment.montant} $
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* ================= MODE + STATUT ================= */}
              <div
                className={`grid grid-cols-2 overflow-hidden rounded-xl border ${
                  isDarkMode ? "border-[#30363D]" : "border-gray-200"
                }`}
              >
                {/* Mode paiement */}
                <div
                  className={`flex items-center gap-3 border-r px-4 py-3 ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  }`}
                >
                  <div
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                      isDarkMode
                        ? "bg-[#21262D] text-gray-400"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    <i className="fas fa-money-bill-wave text-[9px]" />
                  </div>

                  <div>
                    <p className="text-[8px] font-medium uppercase tracking-wide text-gray-500">
                      Mode de paiement
                    </p>

                    <p
                      className={`mt-0.5 text-[11px] font-semibold ${
                        isDarkMode ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      {"Espèce"}
                    </p>
                  </div>
                </div>

                {/* Statut */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <div
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                      selectedPayment.status === 1
                        ? isDarkMode
                          ? "bg-emerald-500/10 text-emerald-400"
                          : "bg-emerald-50 text-emerald-600"
                        : isDarkMode
                          ? "bg-red-500/10 text-red-400"
                          : "bg-red-50 text-red-600"
                    }`}
                  >
                    <i
                      className={`fas ${
                        selectedPayment.status === 1 ? "fa-check" : "fa-times"
                      } text-[9px]`}
                    />
                  </div>

                  <div>
                    <p className="text-[8px] font-medium uppercase tracking-wide text-gray-500">
                      Statut
                    </p>

                    <span
                      className={`mt-0.5 inline-flex items-center gap-1.5 text-[11px] font-semibold ${
                        selectedPayment.status === 1
                          ? "text-emerald-500"
                          : "text-red-500"
                      }`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          selectedPayment.status === 1
                            ? "bg-emerald-500"
                            : "bg-red-500"
                        }`}
                      />

                      {selectedPayment.status === 1 ? "Payé" : "Annulé"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ================= FOOTER ================= */}
            <div
              className={`flex justify-end border-t px-5 py-3 ${
                isDarkMode
                  ? "border-[#30363D] bg-[#0D1117]/40"
                  : "border-gray-200 bg-gray-50/70"
              }`}
            >
              <button
                onClick={() => {
                  setSelectedPayment(null);
                  setShowReceiptModal(false);
                }}
                className={`flex h-9 items-center justify-center gap-2 rounded-lg border px-4 text-xs font-medium transition-colors ${
                  isDarkMode
                    ? "border-[#30363D] bg-[#161B22] text-gray-300 hover:bg-[#21262D]"
                    : "border-gray-200 bg-white text-gray-600 hover:bg-gray-100"
                }`}
              >
                <i className="fas fa-times text-[9px]" />
                Fermer
              </button>
            </div>

            {/* ================= EMAIL FORM ================= */}
            <div
              id="email-form"
              className={`border-t p-5 ${
                isDarkMode
                  ? "border-[#30363D] bg-[#0D1117]/30"
                  : "border-gray-200 bg-gray-50/50"
              }`}
              style={{ display: "none" }}
            >
              <form
                onSubmit={(e) => {
                  e.preventDefault();

                  const emailForm = document.getElementById("email-form");

                  if (emailForm) {
                    emailForm.style.display = "none";
                  }

                  // Show success notification
                  const notification = document.createElement("div");

                  notification.className = `fixed bottom-4 right-4 px-6 py-3 rounded-lg text-white ${
                    isDarkMode ? "bg-green-600" : "bg-green-500"
                  } shadow-lg transition-opacity duration-500`;

                  notification.innerHTML =
                    '<i class="fas fa-check-circle mr-2"></i>Email envoyé avec succès';

                  document.body.appendChild(notification);

                  setTimeout(() => {
                    notification.style.opacity = "0";

                    setTimeout(() => notification.remove(), 500);
                  }, 3000);
                }}
              >
                {/* Email Header */}
                <div className="mb-4 flex items-center gap-3">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                      isDarkMode
                        ? "bg-blue-500/10 text-blue-400"
                        : "bg-blue-50 text-blue-600"
                    }`}
                  >
                    <i className="fas fa-envelope text-[10px]" />
                  </div>

                  <div>
                    <p
                      className={`text-xs font-semibold ${
                        isDarkMode ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      Envoyer le reçu
                    </p>

                    <p className="text-[9px] text-gray-500">
                      Envoyer une copie du reçu par email
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Email */}
                  <div>
                    <label
                      className={`mb-1.5 block text-[10px] font-semibold ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Email du destinataire
                    </label>

                    <div className="relative">
                      <i className="fas fa-envelope absolute left-3.5 top-1/2 -translate-y-1/2 text-[9px] text-gray-500" />

                      <input
                        type="email"
                        defaultValue="parent@email.com"
                        required
                        className={`h-10 w-full rounded-lg border pl-9 pr-3 text-xs outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 ${
                          isDarkMode
                            ? "border-[#30363D] bg-[#0D1117] text-gray-200"
                            : "border-gray-200 bg-white text-gray-700"
                        }`}
                      />
                    </div>
                  </div>

                  {/* Objet */}
                  <div>
                    <label
                      className={`mb-1.5 block text-[10px] font-semibold ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Objet
                    </label>

                    <input
                      type="text"
                      defaultValue={`Reçu de paiement - ${selectedPayment?.type}`}
                      required
                      className={`h-10 w-full rounded-lg border px-3 text-xs outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 ${
                        isDarkMode
                          ? "border-[#30363D] bg-[#0D1117] text-gray-200"
                          : "border-gray-200 bg-white text-gray-700"
                      }`}
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label
                      className={`mb-1.5 block text-[10px] font-semibold ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Message
                    </label>

                    <textarea
                      defaultValue={`Bonjour,\n\nVeuillez trouver ci-joint le reçu de paiement pour ${selectedPayment?.type} de ${selectedPayment?.eleve}.\n\nCordialement,\nEduGest`}
                      required
                      rows={4}
                      className={`w-full resize-none rounded-lg border px-3 py-2.5 text-xs leading-relaxed outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 ${
                        isDarkMode
                          ? "border-[#30363D] bg-[#0D1117] text-gray-200"
                          : "border-gray-200 bg-white text-gray-700"
                      }`}
                    />
                  </div>
                </div>

                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const emailForm = document.getElementById("email-form");

                      if (emailForm) {
                        emailForm.style.display = "none";
                      }
                    }}
                    className={`flex h-9 items-center justify-center gap-2 rounded-lg border px-4 text-xs font-medium transition-colors ${
                      isDarkMode
                        ? "border-[#30363D] bg-[#161B22] text-gray-300 hover:bg-[#21262D]"
                        : "border-gray-200 bg-white text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <i className="fas fa-times text-[9px]" />
                    Annuler
                  </button>

                  <button
                    type="submit"
                    className="flex h-9 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-xs font-medium text-white transition-colors hover:bg-blue-700"
                  >
                    <i className="fas fa-paper-plane text-[9px]" />
                    Envoyer
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL DE MOT DE PASSE ===== */}
      {pendingPaiementAction && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-black/65 backdrop-blur-sm p-4">
          <div
            className={`relative w-full max-w-md overflow-hidden rounded-2xl border shadow-2xl ${
              isDarkMode
                ? "border-[#30363D] bg-[#161B22]"
                : "border-gray-200 bg-white"
            }`}
          >
            {/* ================= HEADER ================= */}
            <div
              className={`flex items-start gap-3 border-b px-5 py-4 ${
                isDarkMode ? "border-[#30363D]" : "border-gray-200"
              }`}
            >
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                  isDarkMode
                    ? "bg-blue-500/10 text-blue-400"
                    : "bg-blue-50 text-blue-600"
                }`}
              >
                <i className="fas fa-shield-alt text-sm" />
              </div>

              <div className="min-w-0">
                <h3
                  className={`text-sm font-semibold sm:text-base ${
                    isDarkMode ? "text-gray-100" : "text-gray-900"
                  }`}
                >
                  Confirmation requise
                </h3>

                <p
                  className={`mt-1 text-[10px] leading-relaxed sm:text-[11px] ${
                    isDarkMode ? "text-gray-500" : "text-gray-500"
                  }`}
                >
                  Cette opération nécessite une vérification de votre identité.
                </p>
              </div>
            </div>

            {/* ================= CONTENT ================= */}
            <div className="p-5 sm:p-6">
              {/* Information */}
              <div
                className={`mb-5 flex items-start gap-3 rounded-xl border p-3.5 ${
                  isDarkMode
                    ? "border-blue-500/20 bg-blue-500/[0.05]"
                    : "border-blue-100 bg-blue-50/60"
                }`}
              >
                <div
                  className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                    isDarkMode
                      ? "bg-blue-500/10 text-blue-400"
                      : "bg-blue-100 text-blue-600"
                  }`}
                >
                  <i className="fas fa-info text-[10px]" />
                </div>

                <div>
                  <p
                    className={`text-[11px] font-semibold ${
                      isDarkMode ? "text-blue-300" : "text-blue-700"
                    }`}
                  >
                    Vérification de sécurité
                  </p>

                  <p
                    className={`mt-1 text-[10px] leading-relaxed ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Veuillez entrer votre mot de passe pour confirmer valider
                    l'opération.
                  </p>
                </div>
              </div>

              {/* Password */}
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <div
                    className={`flex h-7 w-7 items-center justify-center rounded-lg ${
                      isDarkMode
                        ? "bg-[#21262D] text-gray-400"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    <i className="fas fa-key text-[10px]" />
                  </div>

                  <label
                    className={`text-[11px] font-semibold ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Mot de passe
                  </label>

                  <span className="text-red-500">*</span>
                </div>

                <div
                  className={`relative overflow-hidden rounded-xl border transition-colors focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/10 ${
                    isDarkMode
                      ? "border-[#30363D] bg-[#0D1117]"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <div
                    className={`pointer-events-none absolute inset-y-0 left-0 flex w-11 items-center justify-center border-r ${
                      isDarkMode
                        ? "border-[#30363D] text-gray-500"
                        : "border-gray-200 text-gray-400"
                    }`}
                  >
                    <i className="fas fa-lock text-[10px]" />
                  </div>

                  <input
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`h-11 w-full bg-transparent pl-14 pr-4 text-xs outline-none ${
                      isDarkMode
                        ? "text-gray-200 placeholder:text-gray-600"
                        : "text-gray-700 placeholder:text-gray-400"
                    }`}
                    placeholder="Entrez votre mot de passe"
                  />
                </div>

                <div
                  className={`mt-2 flex items-center gap-1.5 text-[9px] ${
                    isDarkMode ? "text-gray-600" : "text-gray-400"
                  }`}
                >
                  <i className="fas fa-lock text-[8px]" />
                  <span>
                    Votre mot de passe est requis pour autoriser cette
                    opération.
                  </span>
                </div>
              </div>
            </div>

            {/* ================= FOOTER ================= */}
            <div
              className={`flex flex-col-reverse gap-2 border-t px-5 py-4 sm:flex-row sm:items-center sm:justify-end ${
                isDarkMode
                  ? "border-[#30363D] bg-[#0D1117]/40"
                  : "border-gray-200 bg-gray-50/70"
              }`}
            >
              {/* ANNULER */}
              <button
                disabled={
                  mutationLogin.isPending ||
                  mutationAnnulerPayment.isPending ||
                  mutationCreatePayment.isPending
                }
                onClick={() => {
                  setPendingPaiementAction(null);
                  setPassword("");
                  setcreatePaymentForm({
                    date: "",
                    amount: "",
                    type: "",
                    method: "Espèce",
                    status: 1,
                    selectedStudentId: null,
                    isCreation: false,
                  });
                }}
                className={`flex h-10 items-center justify-center gap-2 rounded-lg border px-4 text-xs font-medium transition-colors ${
                  isDarkMode
                    ? "border-[#30363D] bg-[#161B22] text-gray-300 hover:bg-[#21262D]"
                    : "border-gray-200 bg-white text-gray-600 hover:bg-gray-100"
                } ${
                  (mutationLogin.isPending ||
                    mutationAnnulerPayment.isPending ||
                    mutationCreatePayment.isPending) &&
                  "cursor-not-allowed opacity-50"
                }`}
              >
                <i className="fas fa-times text-[9px]" />
                Annuler
              </button>

              {/* CONFIRMER */}
              <button
                disabled={
                  mutationLogin.isPending ||
                  mutationAnnulerPayment.isPending ||
                  mutationCreatePayment.isPending
                }
                onClick={() => {
                  handleConfirmPassword();
                }}
                className={`flex h-10 min-w-[120px] items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 ${
                  (mutationLogin.isPending ||
                    mutationAnnulerPayment.isPending ||
                    mutationCreatePayment.isPending) &&
                  "cursor-not-allowed opacity-60"
                }`}
              >
                {mutationLogin.isPending ||
                mutationAnnulerPayment.isPending ||
                mutationCreatePayment.isPending ? (
                  <>
                    <i className="fas fa-circle-notch fa-spin text-[10px]" />
                    <span>Traitement en cours...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-check text-[10px]" />
                    <span>Confirmer</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== NOTIFICATION ===== */}
      {notification && (
        <div
          className={`fixed bottom-5 right-5 max-w-sm px-4 py-3 rounded-xl text-white text-xs font-medium shadow-2xl transition-all duration-500 z-[60] ${
            notification.type === "success" ? "bg-green-500" : "bg-red-500"
          }`}
        >
          {notification.message}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showCancelModal && paymentToCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/65 backdrop-blur-sm p-4">
          <div
            className={`relative w-full max-w-md overflow-hidden rounded-2xl border shadow-2xl ${
              isDarkMode
                ? "border-[#30363D] bg-[#161B22]"
                : "border-gray-200 bg-white"
            }`}
          >
            {/* ================= HEADER ================= */}
            <div
              className={`flex items-center justify-between gap-4 border-b px-5 py-3.5 ${
                isDarkMode ? "border-[#30363D]" : "border-gray-200"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                    isDarkMode
                      ? "bg-red-500/10 text-red-400"
                      : "bg-red-50 text-red-600"
                  }`}
                >
                  <i className="fas fa-exclamation-triangle text-xs" />
                </div>

                <div>
                  <h3
                    className={`text-sm font-semibold ${
                      isDarkMode ? "text-gray-100" : "text-gray-900"
                    }`}
                  >
                    Annuler le paiement
                  </h3>

                  <p className="mt-0.5 text-[9px] text-gray-500">
                    Confirmation d'une opération irréversible
                  </p>
                </div>
              </div>

              {/* Fermer */}
              <button
                type="button"
                disabled={mutationAnnulerPayment.isPending}
                onClick={() => {
                  setShowCancelModal(false);
                  setPaymentToCancel(null);
                }}
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors ${
                  isDarkMode
                    ? "border-[#30363D] text-gray-400 hover:bg-[#21262D] hover:text-white"
                    : "border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                } ${
                  mutationAnnulerPayment.isPending
                    ? "cursor-not-allowed opacity-50"
                    : ""
                }`}
                title="Fermer"
              >
                <i className="fas fa-times text-[10px]" />
              </button>
            </div>

            {/* ================= CONTENT ================= */}
            <div className="p-5">
              {/* Alerte principale */}
              <div
                className={`flex items-start gap-3 rounded-xl border p-4 ${
                  isDarkMode
                    ? "border-red-500/20 bg-red-500/[0.05]"
                    : "border-red-100 bg-red-50/60"
                }`}
              >
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                    isDarkMode
                      ? "bg-red-500/10 text-red-400"
                      : "bg-red-100 text-red-600"
                  }`}
                >
                  <i className="fas fa-exclamation-triangle text-xs" />
                </div>

                <div className="min-w-0">
                  <p
                    className={`text-xs font-semibold ${
                      isDarkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    Confirmer l'annulation
                  </p>

                  <p
                    className={`mt-1.5 text-[11px] leading-relaxed ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Êtes-vous sûr de vouloir annuler ce paiement de{" "}
                    <span
                      className={`font-semibold ${
                        isDarkMode ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      {paymentToCancel.montant} {" $"}
                    </span>{" "}
                    ?
                  </p>
                </div>
              </div>

              {/* Montant du paiement */}
              <div
                className={`mt-3 flex items-center justify-between rounded-xl border px-4 py-3 ${
                  isDarkMode
                    ? "border-[#30363D] bg-[#0D1117]"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                      isDarkMode
                        ? "bg-[#21262D] text-gray-400"
                        : "bg-white text-gray-500"
                    }`}
                  >
                    <i className="fas fa-money-bill-wave text-[10px]" />
                  </div>

                  <div>
                    <p className="text-[9px] font-medium uppercase tracking-wide text-gray-500">
                      Montant du paiement
                    </p>

                    <p
                      className={`mt-0.5 text-sm font-bold tabular-nums ${
                        isDarkMode ? "text-gray-100" : "text-gray-900"
                      }`}
                    >
                      {paymentToCancel.montant} $
                    </p>
                  </div>
                </div>

                {/* Badge */}
                <div
                  className={`flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[9px] font-semibold ${
                    isDarkMode
                      ? "bg-red-500/10 text-red-400"
                      : "bg-red-50 text-red-600"
                  }`}
                >
                  <i className="fas fa-ban text-[8px]" />
                  Annulation
                </div>
              </div>

              {/* Message irréversible */}
              <div className="mt-3 flex items-center gap-2 px-1">
                <i className="fas fa-info-circle text-[9px] text-red-500" />

                <p
                  className={`text-[9px] ${
                    isDarkMode ? "text-gray-500" : "text-gray-500"
                  }`}
                >
                  Cette action est irréversible et ne pourra pas être annulée.
                </p>
              </div>
            </div>

            {/* ================= FOOTER ================= */}
            <div
              className={`flex flex-col-reverse gap-2 border-t px-5 py-3.5 sm:flex-row sm:items-center sm:justify-end ${
                isDarkMode
                  ? "border-[#30363D] bg-[#0D1117]/40"
                  : "border-gray-200 bg-gray-50/70"
              }`}
            >
              {/* ANNULER */}
              <button
                disabled={mutationAnnulerPayment.isPending}
                onClick={() => {
                  setShowCancelModal(false);
                  setPaymentToCancel(null);
                }}
                className={`flex h-10 items-center justify-center gap-2 rounded-lg border px-4 text-xs font-medium transition-colors ${
                  isDarkMode
                    ? "border-[#30363D] bg-[#161B22] text-gray-300 hover:bg-[#21262D]"
                    : "border-gray-200 bg-white text-gray-600 hover:bg-gray-100"
                } ${
                  mutationAnnulerPayment.isPending
                    ? "cursor-not-allowed opacity-50"
                    : ""
                }`}
              >
                <i className="fas fa-times text-[9px]" />
                Annuler
              </button>

              {/* CONFIRMER ANNULATION */}
              <button
                onClick={() => {
                  setShowCancelModal(false);

                  setPendingPaiementAction("cancel");
                }}
                className="flex h-10 items-center justify-center gap-2 rounded-lg bg-red-600 px-5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-red-700"
              >
                <i className="fas fa-ban text-[9px]" />
                Confirmer l'annulation
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add Payment Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/65 backdrop-blur-sm p-4">
          <div
            className={`relative w-full max-w-2xl overflow-hidden rounded-2xl border shadow-2xl ${
              isDarkMode
                ? "border-[#30363D] bg-[#161B22]"
                : "border-gray-200 bg-white"
            }`}
          >
            {/* ================= HEADER ================= */}
            <div
              className={`flex items-center justify-between gap-4 border-b px-5 py-4 sm:px-6 ${
                isDarkMode ? "border-[#30363D]" : "border-gray-200"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                    isDarkMode
                      ? "bg-blue-500/10 text-blue-400"
                      : "bg-blue-50 text-blue-600"
                  }`}
                >
                  <i className="fas fa-cash-register text-sm" />
                </div>

                <div>
                  <h3
                    className={`text-sm font-semibold sm:text-base ${
                      isDarkMode ? "text-gray-100" : "text-gray-900"
                    }`}
                  >
                    Nouveau paiement
                  </h3>

                  <p
                    className={`mt-0.5 text-[10px] sm:text-[11px] ${
                      isDarkMode ? "text-gray-500" : "text-gray-500"
                    }`}
                  >
                    Enregistrez un nouveau paiement pour un élève
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  setcreatePaymentForm({
                    date: "",
                    amount: "",
                    type: "",
                    method: "Espèce",
                    status: 1,
                    selectedStudentId: null,
                    isCreation: false,
                  });
                  setShowAddModal(false);
                }}
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors ${
                  isDarkMode
                    ? "border-[#30363D] text-gray-400 hover:bg-[#21262D] hover:text-white"
                    : "border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                }`}
                title="Fermer"
              >
                <i className="fas fa-times text-xs" />
              </button>
            </div>

            {/* ================= CONTENT ================= */}
            <div className="p-5 sm:p-6">
              <div className="space-y-5">
                {/* ================= ELEVE ================= */}
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <div
                      className={`flex h-7 w-7 items-center justify-center rounded-lg ${
                        isDarkMode
                          ? "bg-[#21262D] text-gray-400"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      <i className="fas fa-user-graduate text-[10px]" />
                    </div>

                    <label
                      className={`text-[11px] font-semibold ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Elève
                    </label>

                    <span className="text-red-500">*</span>
                  </div>

                  <div
                    className={`relative overflow-hidden rounded-xl transition-colors ${
                      isDarkMode
                        ? "border-[#30363D] bg-[#0D1117] focus-within:border-blue-500"
                        : "border-gray-200 bg-white focus-within:border-blue-500"
                    } focus-within:ring-2 focus-within:ring-blue-500/10`}
                  >
                    <div
                      className={`pointer-events-none absolute inset-y-0 left-0 flex w-11 items-center justify-center border-r ${
                        isDarkMode
                          ? "border-[#30363D] text-gray-500"
                          : "border-gray-200 text-gray-400"
                      }`}
                    >
                      <i className="fas fa-user text-[10px]" />
                    </div>

                    <select
                      value={createPaymentForm.selectedStudentId}
                      onChange={(e) =>
                        setcreatePaymentForm({
                          ...createPaymentForm,
                          selectedStudentId: e.target.value,
                        })
                      }
                      className={`h-11 max-h-60 w-full appearance-none pl-14 pr-10 text-xs outline-none ${
                        isDarkMode
                          ? "text-gray-200 bg-[#0D1117]"
                          : "text-gray-700"
                      }`}
                    >
                      <option value="">Sélectionner un élève</option>

                      {studentsNowAffected.length === 0 ? (
                        <option value="" disabled>
                          Aucun élève affecté à l'année scolaire sélectionnée
                        </option>
                      ) : (
                        studentsNowAffected
                          .sort((a: any, b: any) =>
                            a.student.nom.localeCompare(b.student.nom),
                          )
                          .map(
                            (
                              { student, classeDesignation }: any,
                              index: number,
                            ) => (
                              <option
                                key={student.eleve_id}
                                value={student.eleve_id}
                              >
                                {index + 1} -{" "}
                                {`${student.nom.toUpperCase()} ${student.postnom.toUpperCase()} ${capitalizeFirstLetter(
                                  student.prenom,
                                )} - (${capitalizeFirstLetter(
                                  classeDesignation,
                                )})`}
                              </option>
                            ),
                          )
                      )}
                    </select>

                    <div className="pointer-events-none absolute inset-y-0 right-0 flex w-10 items-center justify-center text-gray-500">
                      <i className="fas fa-chevron-down text-[9px]" />
                    </div>
                  </div>
                </div>

                {/* ================= TYPE + MONTANT ================= */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {/* TYPE DE FRAIS */}
                  <div>
                    <div className="mb-2 flex items-center gap-2">
                      <div
                        className={`flex h-7 w-7 items-center justify-center rounded-lg ${
                          isDarkMode
                            ? "bg-blue-500/10 text-blue-400"
                            : "bg-blue-50 text-blue-600"
                        }`}
                      >
                        <i className="fas fa-file-invoice-dollar text-[10px]" />
                      </div>

                      <label
                        className={`text-[11px] font-semibold ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Type de frais
                      </label>

                      <span className="text-red-500">*</span>
                    </div>

                    <div
                      className={`relative overflow-hidden rounded-xl border transition-colors ${
                        isDarkMode
                          ? "border-[#30363D] bg-[#0D1117] focus-within:border-blue-500"
                          : "border-gray-200 bg-white focus-within:border-blue-500"
                      } focus-within:ring-2 focus-within:ring-blue-500/10`}
                    >
                      <div
                        className={`pointer-events-none absolute inset-y-0 left-0 flex w-11 items-center justify-center border-r ${
                          isDarkMode
                            ? "border-[#30363D] text-blue-400"
                            : "border-gray-200 text-blue-500"
                        }`}
                      >
                        <i className="fas fa-receipt text-[10px]" />
                      </div>

                      <select
                        value={createPaymentForm.type}
                        onChange={(e) =>
                          setcreatePaymentForm((prev) => ({
                            ...prev,
                            type: e.target.value,
                          }))
                        }
                        className={`h-11 w-full appearance-none bg-transparent pl-14 pr-10 text-xs outline-none ${
                          isDarkMode ? "text-gray-200" : "text-gray-700"
                        }`}
                      >
                        <option value={1}>Frais de scolarité</option>
                      </select>

                      <div className="pointer-events-none absolute inset-y-0 right-0 flex w-10 items-center justify-center text-gray-500">
                        <i className="fas fa-chevron-down text-[9px]" />
                      </div>
                    </div>
                  </div>

                  {/* MONTANT */}
                  <div>
                    <div className="mb-2 flex items-center gap-2">
                      <div
                        className={`flex h-7 w-7 items-center justify-center rounded-lg ${
                          isDarkMode
                            ? "bg-emerald-500/10 text-emerald-400"
                            : "bg-emerald-50 text-emerald-600"
                        }`}
                      >
                        <i className="fas fa-dollar-sign text-[10px]" />
                      </div>

                      <label
                        className={`text-[11px] font-semibold ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Montant ($)
                      </label>

                      <span className="text-red-500">*</span>
                    </div>

                    <div
                      className={`relative overflow-hidden rounded-xl border transition-colors ${
                        isDarkMode
                          ? "border-[#30363D] bg-[#0D1117] focus-within:border-blue-500"
                          : "border-gray-200 bg-white focus-within:border-blue-500"
                      } focus-within:ring-2 focus-within:ring-blue-500/10`}
                    >
                      <div
                        className={`pointer-events-none absolute inset-y-0 left-0 flex w-11 items-center justify-center border-r font-semibold ${
                          isDarkMode
                            ? "border-[#30363D] text-emerald-400"
                            : "border-gray-200 text-emerald-600"
                        }`}
                      >
                        $
                      </div>

                      <input
                        type="number"
                        value={createPaymentForm.amount}
                        onChange={(e) =>
                          setcreatePaymentForm((prev) => ({
                            ...prev,
                            amount: Number(e.target.value),
                          }))
                        }
                        className={`h-11 w-full bg-transparent pl-14 pr-4 text-sm font-semibold outline-none ${
                          isDarkMode
                            ? "text-gray-100 placeholder:text-gray-600"
                            : "text-gray-800 placeholder:text-gray-400"
                        }`}
                        placeholder="0,00"
                        required
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>

                {/* ================= INFORMATION ================= */}
                <div
                  className={`flex items-start gap-3 rounded-xl border p-3.5 ${
                    isDarkMode
                      ? "border-blue-500/20 bg-blue-500/[0.05]"
                      : "border-blue-100 bg-blue-50/60"
                  }`}
                >
                  <div
                    className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                      isDarkMode
                        ? "bg-blue-500/10 text-blue-400"
                        : "bg-blue-100 text-blue-600"
                    }`}
                  >
                    <i className="fas fa-info text-[10px]" />
                  </div>

                  <div>
                    <p
                      className={`text-[11px] font-semibold ${
                        isDarkMode ? "text-blue-300" : "text-blue-700"
                      }`}
                    >
                      Information importante
                    </p>

                    <p
                      className={`mt-1 text-[10px] leading-relaxed ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Le montant à payer ne doit pas dépasser le solde restant
                      de l'élève.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ================= FOOTER ================= */}
            <div
              className={`flex flex-col-reverse gap-2 border-t px-5 py-4 sm:flex-row sm:items-center sm:justify-end sm:px-6 ${
                isDarkMode
                  ? "border-[#30363D] bg-[#0D1117]/40"
                  : "border-gray-200 bg-gray-50/70"
              }`}
            >
              <button
                onClick={() => {
                  setcreatePaymentForm({
                    date: "",
                    amount: "",
                    type: "",
                    method: "Espèce",
                    status: 1,
                    selectedStudentId: null,
                    isCreation: false,
                  });
                  setShowAddModal(false);
                }}
                className={`flex h-10 items-center justify-center gap-2 rounded-lg border px-4 text-xs font-medium transition-colors ${
                  isDarkMode
                    ? "border-[#30363D] bg-[#161B22] text-gray-300 hover:bg-[#21262D]"
                    : "border-gray-200 bg-white text-gray-600 hover:bg-gray-100"
                }`}
              >
                <i className="fas fa-times text-[9px]" />
                Annuler
              </button>

              <button
                onClick={() => {
                  if (!createPaymentForm.amount)
                    return showNotification(
                      "Le montant à payer ne doit pas être nul s'il vous plait",
                      "error",
                    );

                  if (!createPaymentForm.selectedStudentId)
                    return showNotification(
                      "Veuillez selectionner un élève s'il vous plait",
                      "error",
                    );

                  if (
                    createPaymentForm.amount >
                    getPaiementDetailForSelectedStudent()?.balance
                  )
                    return showNotification(
                      "Le montant à payer ne doit pas dépasser le solde restant de " +
                        getPaiementDetailForSelectedStudent()?.balance +
                        " $",
                      "error",
                    );

                  setPassword("");
                  setSearchQuery("");

                  setcreatePaymentForm((prev) => ({
                    ...prev,
                    isCreation: true,
                  }));

                  setShowAddModal(false);
                  setPendingPaiementAction("create");
                }}
                className="flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
              >
                <i className="fas fa-save text-[10px]" />
                Enregistrer le paiement
              </button>
            </div>

            {/* ================= EMAIL FORM ================= */}
            <div
              id="email-form"
              className={`border-t p-5 sm:p-6 ${
                isDarkMode
                  ? "border-[#30363D] bg-[#0D1117]/30"
                  : "border-gray-200 bg-gray-50/50"
              }`}
              style={{ display: "none" }}
            >
              <form
                onSubmit={(e) => {
                  e.preventDefault();

                  const emailForm = document.getElementById("email-form");

                  if (emailForm) {
                    emailForm.style.display = "none";
                  }

                  // Show success notification
                  const notification = document.createElement("div");

                  notification.className = `fixed bottom-4 right-4 px-6 py-3 rounded-lg text-white ${
                    isDarkMode ? "bg-green-600" : "bg-green-500"
                  } shadow-lg transition-opacity duration-500`;

                  notification.innerHTML =
                    '<i class="fas fa-check-circle mr-2"></i>Email envoyé avec succès';

                  document.body.appendChild(notification);

                  setTimeout(() => {
                    notification.style.opacity = "0";

                    setTimeout(() => notification.remove(), 500);
                  }, 3000);
                }}
              >
                <div className="mb-5 flex items-center gap-3">
                  <div
                    className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                      isDarkMode
                        ? "bg-blue-500/10 text-blue-400"
                        : "bg-blue-50 text-blue-600"
                    }`}
                  >
                    <i className="fas fa-envelope text-xs" />
                  </div>

                  <div>
                    <p
                      className={`text-xs font-semibold ${
                        isDarkMode ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      Envoyer le reçu
                    </p>

                    <p className="mt-0.5 text-[10px] text-gray-500">
                      Envoyer une copie du reçu par email
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label
                      className={`mb-1.5 block text-[11px] font-semibold ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Email du destinataire
                    </label>

                    <div className="relative">
                      <i className="fas fa-envelope absolute left-3.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-500" />

                      <input
                        type="email"
                        defaultValue="parent@email.com"
                        required
                        className={`h-10 w-full rounded-lg border pl-9 pr-3 text-xs outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 ${
                          isDarkMode
                            ? "border-[#30363D] bg-[#0D1117] text-gray-200"
                            : "border-gray-200 bg-white text-gray-700"
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      className={`mb-1.5 block text-[11px] font-semibold ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Objet
                    </label>

                    <input
                      type="text"
                      defaultValue={`Reçu de paiement - ${selectedPayment?.type}`}
                      required
                      className={`h-10 w-full rounded-lg border px-3 text-xs outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 ${
                        isDarkMode
                          ? "border-[#30363D] bg-[#0D1117] text-gray-200"
                          : "border-gray-200 bg-white text-gray-700"
                      }`}
                    />
                  </div>

                  <div>
                    <label
                      className={`mb-1.5 block text-[11px] font-semibold ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Message
                    </label>

                    <textarea
                      defaultValue={`Bonjour,\n\nVeuillez trouver ci-joint le reçu de paiement pour ${selectedPayment?.type} de ${selectedPayment?.eleve}.\n\nCordialement,\nEduGest`}
                      required
                      rows={4}
                      className={`w-full resize-none rounded-lg border px-3 py-2.5 text-xs leading-relaxed outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 ${
                        isDarkMode
                          ? "border-[#30363D] bg-[#0D1117] text-gray-200"
                          : "border-gray-200 bg-white text-gray-700"
                      }`}
                    />
                  </div>
                </div>

                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const emailForm = document.getElementById("email-form");

                      if (emailForm) {
                        emailForm.style.display = "none";
                      }
                    }}
                    className={`h-9 rounded-lg border px-4 text-xs font-medium transition-colors ${
                      isDarkMode
                        ? "border-[#30363D] bg-[#161B22] text-gray-300 hover:bg-[#21262D]"
                        : "border-gray-200 bg-white text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    Annuler
                  </button>

                  <button
                    type="submit"
                    className="flex h-9 items-center gap-2 rounded-lg bg-blue-600 px-4 text-xs font-medium text-white transition-colors hover:bg-blue-700"
                  >
                    <i className="fas fa-paper-plane text-[9px]" />
                    Envoyer
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <MyLoader
        isLoading={
          mutationAnnulerPayment.isPending || mutationCreatePayment.isPending
        }
        message={
          mutationAnnulerPayment.isPending
            ? "Annulation en cours..."
            : mutationCreatePayment.isPending
              ? "Paiement en cours..."
              : ""
        }
      />
      <MyLoader
        isLoading={mutationLogin.isPending}
        message="Vérification en cours..."
      />
    </div>
  );
};
export default Payment;
